import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TextEditorStyle } from '../../../../../../../shared/components/text-editor/text-editor.component';

export interface InfoLayout1Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch' | 'cover' | 'contain';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: TextEditorStyle;
}

/**
 * Preview component for the "Info Layout 1" panel used by the website builder; renders heading, paragraph, image and action button with customizable styles and emits button click events.
 * @component
 * @selector app-info-layout-1-preview
 * @example
 *   <app-info-layout-1-preview
 *     [data]="sampleInfoLayoutData"
 *     [headingColor]="'#222'"
 *     [paragraphColor]="'#666'"
 *     [headingFont]="'Roboto'"
 *     [bodyFont]="'Open Sans'"
 *     [primaryColor]="'#ff6a00'"
 *     [titleStyles]="{ fontWeight: '700' }"
 *     (buttonClick)="handleButtonClick($event)">
 *   </app-info-layout-1-preview>
 * @input {InfoLayout1Data | null} data - The data model for the layout, including title, paragraph, image metadata (src, alt, scaletype) and button/link info.
 * @input {string | null} headingColor - Optional color value for the heading text.
 * @input {string | null} paragraphColor - Optional color value for the paragraph/body text.
 * @input {string | null} headingFont - Optional font family for the heading.
 * @input {string | null} bodyFont - Optional font family for body/paragraph text.
 * @input {string | null} primaryColor - Optional primary/theme color used for accents and buttons.
 * @input {any} titleStyles - Optional additional inline style overrides for the title element.
 * @output {EventEmitter<{ link: string; event: Event }>} buttonClick - Emitted when an action button is activated; includes the target link (if provided) and the originating DOM event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): Emits the buttonClick event when a valid link is provided; guards against undefined links.
 * - getImageSrc(): Returns the best available image source from data.image.src or data.imageUrl, or an empty string if none.
 * - getImageAlt(): Returns the image alt text from data or falls back to a sensible default ("Car Detailing").
 * - getImageObjectFit(): Maps the data-provided scaletype to an appropriate CSS object-fit value (cover, contain, fill).
 *
 * Lifecycle hooks:
 * - This component does not implement Angular lifecycle hooks explicitly (no ngOnInit/ngOnChanges required for its basic preview responsibilities); it relies on input bindings and template rendering.
 */
@Component({
  selector: 'app-info-layout-1-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './info-layout-1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./info-layout-1.component.scss'],
})
export class InfoLayout1PreviewComponent {
  @Input() data: InfoLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Detailing';
  }

  /**
   * Returns the CSS object-fit value corresponding to the component's image scaletype.
   * @example
   * etImageObjectFit()
   * 'cover'
   * @returns {string} The CSS object-fit value ('fill', 'contain', or 'cover').
   */
  getImageObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'cover';
    // Map API scaletype to CSS object-fit
    switch (scaletype) {
      case 'fill':
      case 'stretch':
        return 'fill';
      case 'fit':
        return 'contain';
      case 'cover':
        return 'cover';
      case 'contain':
        return 'contain';
      default:
        return 'cover';
    }
  }
}
